import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaModalFooter } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalFooter";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { useTranslation } from "@coworker/locales";
import { memo, useCallback, useMemo, useState } from "react";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import useSaveGuardedFunction from "@coworker/apprestructured/src/save-guard/useSaveGuardedFunction";
import styles from "./AddLocation.module.css";
import ErrorAddOn from "../TaskCreatorAndEditorSheet/ListItem/ErrorAddOn/ErrorAddOn";
import { ItemNoAndType } from "@coworker/apprestructured/src/shared/types/item";
import { GroupedSalesLocations } from "@coworker/apprestructured/src/task-management/types/SalesLocation";
import FixaTabs, {
  FixaTab,
  FixaTabPanel,
} from "@coworker/apprestructured/src/shared/wrappers/FixaTabs/FixaTabs";
import LocationsTab from "./LocationsTab/LocationsTab";
import {
  CustomLocationTab,
  MAX_CUSTOM_LOCATION_LENGTH,
} from "./CustomLocationTab/CustomLocationTab";
import { LocationChange } from "@coworker/apprestructured/src/task-management/types/SalesLocation";
import ListItem from "../TaskCreatorAndEditorSheet/ListItem/ListItem";
interface AddLocationProps {
  onTabChanged: (tabId: AddLocationTab) => void;
  itemNoAndType: ItemNoAndType | null;
  slid: string;
  customLocation: string;
  disabled: boolean;
  onConfirmClick: ({ slid, customLocation }: LocationChange) => void;
  isLoadingLocations: boolean;
  filteredLocations: GroupedSalesLocations;
  totalAssignedSalesSpaceQuantity: number;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  isError?: boolean;
}

export type AddLocationTab = "locations" | "customLocation";

export function getDefaultLocationTab(
  slid: string,
  customLocation: string
): AddLocationTab {
  if (slid) return "locations";
  if (customLocation) return "customLocation";
  return "locations";
}

export const AddLocation = memo(
  ({
    onTabChanged,
    itemNoAndType,
    slid,
    customLocation,
    disabled,
    onConfirmClick,
    isLoadingLocations,
    filteredLocations,
    totalAssignedSalesSpaceQuantity,
    searchQuery,
    setSearchQuery,
    isError = false,
  }: AddLocationProps) => {
    const [showModal, setShowModal] = useState(false);
    const [newSlid, setNewSlid] = useState<string>(slid);
    const [newCustomLocation, setNewCustomLocation] =
      useState<string>(customLocation);

    const defaultLocationTab = useMemo<AddLocationTab>(() => {
      return getDefaultLocationTab(slid, customLocation);
    }, [slid, customLocation]);

    const handleOpenModal = useCallback(() => {
      setNewSlid(slid);
      setNewCustomLocation(customLocation);
      setSearchQuery("");
      onTabChanged(defaultLocationTab);
      setShowModal(true);
    }, [
      slid,
      customLocation,
      setSearchQuery,
      onTabChanged,
      defaultLocationTab,
    ]);

    const {
      saveGuardedFunction: handleCloseModal,
      enableSaveGuard,
      disableSaveGuard,
    } = useSaveGuardedFunction(
      useCallback(() => {
        setShowModal(false);
      }, [])
    );

    const { t } = useTranslation();

    const customLocationError = useMemo(() => {
      if (newCustomLocation.length > MAX_CUSTOM_LOCATION_LENGTH) {
        return t("maximumCharactersLengthString", {
          count: MAX_CUSTOM_LOCATION_LENGTH,
        });
      }
      return null;
    }, [newCustomLocation, t]);

    const handleConfirm = useCallback(() => {
      if (customLocationError) {
        return;
      }
      onConfirmClick({
        slid: newSlid,
        customLocation: newCustomLocation,
      });
      setShowModal(false);
      disableSaveGuard();
    }, [
      customLocationError,
      onConfirmClick,
      newSlid,
      newCustomLocation,
      disableSaveGuard,
    ]);

    const handleSlidChange = useCallback(
      (nextSlid: string) => {
        setNewSlid(nextSlid);
        setNewCustomLocation(""); //Clear the custom location when slid is being edited to avoid confusion, as only one of them can be set.
        enableSaveGuard();
      },
      [enableSaveGuard]
    );

    const handleCustomLocationChange = useCallback(
      (newCustomLocation: string) => {
        setNewCustomLocation(newCustomLocation);
        setNewSlid(""); //Clear the slid when custom location is being edited to avoid confusion, as only one of them can be set.
        enableSaveGuard();
      },
      [enableSaveGuard]
    );

    const handleTabChange = useCallback(
      (tabPanelId: string) => {
        const tab = tabPanelId as AddLocationTab;
        onTabChanged(tab);
      },
      [onTabChanged]
    );

    const description = slid || customLocation;
    const emphasised = !!slid || !!customLocation;

    return (
      <>
        <ListItem
          testId="add-location-list-item"
          title={t("location3String")}
          onClick={handleOpenModal}
          emphasised={emphasised}
          description={description}
          disabled={disabled}
          addOn={
            isError ? (
              <ErrorAddOn testId="add-location-error">
                {t("pleaseFillInString")}
              </ErrorAddOn>
            ) : null
          }
        />
        <FixaModal
          visible={showModal}
          handleCloseBtn={handleCloseModal}
          data-testid="add-location-modal"
        >
          <FixaSheets
            labelledById="task-management-add-location-header"
            header={
              <FixaModalHeader
                id="task-management-add-location-header"
                title={t("location3String")}
                closeBtnClick={handleCloseModal}
              />
            }
            footer={
              <FixaModalFooter renderBorder>
                <FixaButton
                  data-testid="add-location-confirm"
                  type="primary"
                  fluid
                  text={t("confirmString")}
                  onClick={handleConfirm}
                />
              </FixaModalFooter>
            }
            fullSize
          >
            <FixaModalBody>
              <FixaTabs
                className={styles["root"]!}
                defaultActiveTab={defaultLocationTab}
                tabs={[
                  <FixaTab
                    data-testid="add-location-locations-tab"
                    key="locations"
                    tabPanelId="locations"
                    text={t("locationsString")}
                  />,
                  <FixaTab
                    data-testid="add-location-custom-location-tab"
                    key="customLocation"
                    tabPanelId="customLocation"
                    text={t("customLocationString")}
                  />,
                ]}
                onTabChanged={handleTabChange}
                tabPanels={[
                  <FixaTabPanel
                    data-testid="add-location-locations-tab-panel"
                    key="locations-tab-panel"
                    tabPanelId="locations"
                  >
                    <LocationsTab
                      onSlidChange={handleSlidChange}
                      itemNoAndType={itemNoAndType}
                      selectedSlid={newSlid}
                      searchQuery={searchQuery}
                      setSearchQuery={setSearchQuery}
                      isLoadingLocations={isLoadingLocations}
                      filteredLocations={filteredLocations}
                      totalAssignedSalesSpaceQuantity={
                        totalAssignedSalesSpaceQuantity
                      }
                    />
                  </FixaTabPanel>,
                  <FixaTabPanel
                    data-testid="add-location-custom-location-tab-panel"
                    key="customLocation-tab-panel"
                    tabPanelId="customLocation"
                  >
                    <CustomLocationTab
                      customLocation={newCustomLocation}
                      onCustomLocationChange={handleCustomLocationChange}
                      error={customLocationError}
                    />
                  </FixaTabPanel>,
                ]}
              />
            </FixaModalBody>
          </FixaSheets>
        </FixaModal>
      </>
    );
  }
);
